interface Options {
  minWidth?: number;
  maxWidth?: number;
  onDismiss(): void;
}

/** Shared popup boundary for the signer's searchable country and email controls. */
export function createFieldPopup(anchor: HTMLElement, panel: HTMLElement, options: Options) {
  panel.classList.add('field-popup');
  panel.hidden = true;
  document.body.append(panel);
  let open = false;
  const reposition = () => {
    if (!open) return;
    const rect = anchor.getBoundingClientRect();
    if (!anchor.isConnected || !anchor.getClientRects().length) { options.onDismiss(); return; }
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft ?? 0, top = viewport?.offsetTop ?? 0;
    const width = viewport?.width ?? window.innerWidth, height = viewport?.height ?? window.innerHeight;
    const padding = 12, gap = 6;
    const popupWidth = Math.max(0, Math.min(Math.max(rect.width, options.minWidth ?? 260), options.maxWidth ?? 360, width - padding * 2));
    panel.style.width = `${popupWidth}px`;
    const below = Math.max(0, top + height - rect.bottom - padding - gap);
    const above = Math.max(0, rect.top - top - padding - gap);
    const placeBelow = below >= Math.min(300, panel.scrollHeight || 300) || below >= above;
    panel.style.maxHeight = `${Math.max(0, Math.min(360, placeBelow ? below : above))}px`;
    panel.style.left = `${Math.max(left + padding, Math.min(rect.left, left + width - padding - popupWidth))}px`;
    panel.style.top = `${placeBelow ? rect.bottom + gap : Math.max(top + padding, rect.top - gap - panel.getBoundingClientRect().height)}px`;
  };
  const outside = (event: Event) => {
    if (open && event.target instanceof Node && !anchor.contains(event.target) && !panel.contains(event.target)) options.onDismiss();
  };
  const onScroll = (event: Event) => { if (!(event.target instanceof Node) || !panel.contains(event.target)) reposition(); };
  const listen = (add: boolean) => {
    const method = add ? 'addEventListener' : 'removeEventListener';
    document[method]('pointerdown', outside, true);
    document[method]('focusin', outside, true);
    document[method]('scroll', onScroll, true);
    window[method]('resize', reposition);
    window.visualViewport?.[method]('resize', reposition);
    window.visualViewport?.[method]('scroll', reposition);
  };
  const hide = () => { open = false; panel.hidden = true; listen(false); };
  return {
    show() { if (!open) { open = true; panel.hidden = false; listen(true); } reposition(); },
    hide,
    reposition,
    dispose() { hide(); panel.remove(); },
  };
}
